import { Dialog } from '@/components/Dialog/Dialog';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useNavigate } from 'react-router';

import { Button } from '@/components/Elements/Button';
import { useUpgradeModal } from '@/hooks/useUpgradeModal';
import { usePlusFeatures } from '@/features/subscription/hooks/usePlusFeatures';
import { useUserPermission } from '@/lib/auth';
import RWPlusLogo from '@/assets/rw-plus-logo.png';
import { getUpgradeCopy } from '@/lib/copy/upgrade';

export const UpgradeModal = () => {
  const { t } = useTranslation();
  const { isOpen, close } = useUpgradeModal();
  const navigate = useNavigate();
  const features = usePlusFeatures();
  const { data: user } = useUserPermission();
  const canTrial = user ? !user.hasTrialHistory : true;
  const copy = getUpgradeCopy(t);

  const handleStartTrial = () => {
    close();
    navigate({
      pathname: '/subscription',
      search: createSearchParams({ display_basic: 'false' }).toString(),
    });
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={close}
      showCloseButton={false}
      maxWidth="lg"
    >
      <div className="w-full max-w-[1040px] space-y-4 rounded-[28px] bg-white p-6">
        <div className="flex flex-col items-center gap-2 text-center">
          <img
            src={RWPlusLogo}
            alt="RuleWatcher Plus"
            className="h-14 w-auto sm:h-[54px]"
            loading="lazy"
            decoding="async"
          />
          <p className="text-base font-semibold uppercase tracking-[0.2em] text-primary-teal">
            {copy.headline}
          </p>
          <p className="max-w-[560px] text-sm leading-relaxed text-rw-neutral-dark-gray">
            {copy.sub}
          </p>
        </div>

        <div className="rounded-[20px] bg-gradient-to-l from-gradient-teal-start/50 to-gradient-teal-end/50 p-[2px]">
          <div className="divide-y divide-rw-neutral-lighter-gray overflow-hidden rounded-[18px] bg-white">
            {features.map(({ key, icon, title, desc }) => (
              <div
                key={key}
                className="flex items-center gap-3 px-4 py-3 text-left text-rw-neutral-darker-gray"
              >
                <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary-teal/10 text-primary-teal">
                  {icon}
                </div>
                <div className="space-y-1">
                  <p className="text-base font-semibold">{title}</p>
                  <p className="text-sm leading-relaxed text-rw-neutral-dark-gray">
                    {desc}
                  </p>
                  {key === 'csv-download' && canTrial && (
                    <p className="text-xs text-rw-neutral-gray">
                      {copy.trialLimitNote}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col items-center gap-3">
          <Button
            size="lg"
            noBordered
            className="w-full max-w-sm rounded-full border-0 bg-gradient-to-l from-gradient-teal-start to-gradient-teal-end px-8 py-2.5 text-base font-semibold text-white shadow-none hover:opacity-90"
            onClick={handleStartTrial}
          >
            {canTrial ? copy.ctaTrial : copy.ctaUpgrade}
          </Button>
          <button
            type="button"
            onClick={close}
            className="text-sm font-medium text-rw-neutral-dark-gray underline-offset-4 hover:underline"
          >
            {copy.dismiss}
          </button>
        </div>
      </div>
    </Dialog>
  );
};
